"use client";

import { InterestGroup } from "../../../app/storyblok/[localeString]/support-hub/types/contact.types";
import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { useCrhClickCountingContext } from "../../../helpers/CrhClickCountingContext";
import { useTextDirectionContext } from "../../../helpers/TextDirectionContext";
import { SliderButton } from "../../SliderButton/SliderButton";
import { NavigationCard } from "../../contact/NavigationCard/NavigationCard";
import { NavigationCardShort } from "../../contact/NavigationCard/NavigationCardShort";
import { CosmosText } from "@cosmos/web-react";
import classNames from "classnames/bind";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import styles from "./InterestsGroups.module.css";

const cx = classNames.bind(styles);

export default function InterestsGroups({
  interests,
  interestGroupsLabel,
  dragProgress = 0,
  isCompact = false,
  hasMobileCarousel,
  isHomepage,
  isTopOfPage,
  appearance,
  hideTitle,
}: {
  interests: InterestGroup[];
  interestGroupsLabel: string;
  dragProgress?: number;
  isCompact?: boolean;
  hasMobileCarousel: boolean;
  isHomepage?: boolean;
  isTopOfPage?: boolean;
  appearance: "dark" | "light";
  hideTitle?: boolean;
}) {
  const textDirection = useTextDirectionContext();
  const selectedInterestGroupParam = useParams()?.interestGroupSlug;
  const { trackClick } = useCrhClickCountingContext();
  const contactPageRouter = useContactPageRouterContext();
  // While a search is active (`?q=...`), no interest group should appear
  // selected — search results are a separate "view" that supersedes the
  // path's interest-group context.
  const selectedInterestGroup = contactPageRouter.selectedSearchQuery
    ? undefined
    : selectedInterestGroupParam;

  const EMBLA_OPTIONS: EmblaOptionsType = {
    active: hasMobileCarousel ? true : false,
    align: "start",
    direction: textDirection,
    breakpoints: {
      "(width >= 1000px)": { active: false },
    },
  };

  // Embla Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel(EMBLA_OPTIONS);

  // Embla Next/Prev buttons
  const [prevBtnDisabled, setPrevBtnDisabled] = useState<boolean>(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState<boolean>(true);

  // Track if this is the initial mount - if there's already a selected interest group
  // on first render, it means user came from homepage
  const [hasAutoScrolled, setHasAutoScrolled] = useState<boolean>(false);

  // Embla Prev button
  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  // Embla Next button
  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  // Embla onSelect
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    if (!emblaApi) return;
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  // Embla useEffect
  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  // Auto-scroll on initial load if there's a selected interest group (came from homepage)
  useEffect(() => {
    if (!emblaApi || !selectedInterestGroup || isHomepage || hasAutoScrolled)
      return;

    const targetIndex = interests.findIndex(
      (interest) => interest.slug === selectedInterestGroup,
    );

    if (targetIndex !== -1) {
      emblaApi.scrollTo(targetIndex);
      setHasAutoScrolled(true); // Mark as completed so it doesn't happen again
    }
  }, [emblaApi, selectedInterestGroup, interests, isHomepage, hasAutoScrolled]);

  // Homepage variant
  if (isHomepage) {
    return (
      <div className={cx("container")} data-homepage="true">
        <div className={cx("header")} data-visually-hidden={hideTitle}>
          <CosmosText
            tag="h2"
            appearance={appearance}
            size="small"
            weight="medium"
          >
            {interestGroupsLabel}
          </CosmosText>
        </div>
        <div className={cx("embla")}>
          <div className={cx("embla-viewport")}>
            <div className={cx("grid-container-homepage")}>
              {interests.map((item, index) => {
                return (
                  <NavigationCard
                    key={index}
                    isHomepage={isHomepage}
                    href={contactPageRouter.toggleInterestGroupUrl({
                      slug: item.slug,
                    })}
                    onClick={() => void trackClick()}
                    label={item.title ?? ""}
                    backgroundDesktopSrc={item.imageDesktop ?? ""}
                    backgroundMobileSrc={item.imageMobile ?? ""}
                    // iconSrc={item.imageIcon ?? ""}
                    selected={item.slug === selectedInterestGroup}
                    dragProgress={dragProgress}
                    isCompact={isCompact}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Compact
  if (isCompact) {
    return (
      <div
        className={cx("container")}
        data-homepage={undefined}
        data-top-of-page={isTopOfPage}
        data-carousel={hasMobileCarousel}
      >
        <div className={cx("header")} data-visually-hidden={hideTitle}>
          <CosmosText tag="h2" appearance="dark" size="small" weight="medium">
            {interestGroupsLabel}
          </CosmosText>
        </div>
        <div className={cx("embla")}>
          <div className={cx("carousel-nav")}>
            <SliderButton
              kind="previous"
              buttonKind="bare"
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              className={cx("nav-btn", "nav-btn--prev")}
              size="small"
              buttonAppearance="light"
            />
            <SliderButton
              kind="next"
              buttonKind="bare"
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              className={cx("nav-btn", "nav-btn--next")}
              size="small"
              buttonAppearance="light"
            />
          </div>
          <div className={cx("embla-viewport")} ref={emblaRef}>
            <div className={cx("embla-container")}>
              {interests.map((item, index) => {
                return (
                  <div
                    className={cx("embla-slide", "embla-slide--compact")}
                    key={index}
                  >
                    <NavigationCardShort
                      href={contactPageRouter.toggleInterestGroupUrl({
                        slug: item.slug,
                      })}
                      onClick={() => {
                        trackClick();
                        // Snap to the clicked card's position in the carousel
                        if (emblaApi) {
                          emblaApi.scrollTo(index);
                        }
                      }}
                      label={item.title}
                      iconSrc={item.imageIcon}
                      selected={item.slug === selectedInterestGroup}
                      // dragProgress={dragProgress}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default/Contact page variant
  return (
    <div
      className={cx("container")}
      data-homepage={undefined}
      data-top-of-page={isTopOfPage}
      data-carousel={hasMobileCarousel}
    >
      <div className={cx("header")} data-visually-hidden={hideTitle}>
        <CosmosText tag="h2" appearance="dark" size="small" weight="medium">
          {interestGroupsLabel}
        </CosmosText>
      </div>
      <div className={cx("embla")}>
        <div className={cx("carousel-nav")}>
          <SliderButton
            kind="previous"
            buttonKind="bare"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            className={cx("nav-btn", "nav-btn--prev")}
            size="small"
            buttonAppearance="light"
          />
          <SliderButton
            kind="next"
            buttonKind="bare"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            className={cx("nav-btn", "nav-btn--next")}
            size="small"
            buttonAppearance="light"
          />
        </div>
        <div className={cx("embla-viewport")} ref={emblaRef}>
          <div className={cx("embla-container")}>
            {interests.map((item, index) => {
              return (
                <div className={cx("embla-slide")} key={index}>
                  <NavigationCard
                    isHomepage={false}
                    href={contactPageRouter.toggleInterestGroupUrl({
                      slug: item.slug,
                    })}
                    onClick={() => {
                      trackClick();
                      // Snap to the clicked card's position in the carousel
                      if (emblaApi) {
                        emblaApi.scrollTo(index);
                      }
                    }}
                    label={item.title}
                    backgroundDesktopSrc={item.imageDesktop}
                    backgroundMobileSrc={item.imageMobile}
                    // iconSrc={item.imageIcon}
                    selected={item.slug === selectedInterestGroup}
                    dragProgress={dragProgress}
                    isCompact={isCompact}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
